<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue'
import { useScroll, watchThrottled } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { useChapterTheme } from './composables/useChapterTheme'
import { useInfiniteChapter } from './composables/useInfiniteChapter'
import { useChapterVisibility } from './composables/useChapterVisibility'
import ScrollChapterItemDefault from './components/ScrollChapterItem.vue'
import ScrollChapterItemFlipnovel from './components/ScrollChapterItem.flipnovel.vue'
import ChapterThemeSettingsDefault from './components/ChapterThemeSettings.vue'
import ChapterThemeSettingsFlipnovel from './components/ChapterThemeSettings.flipnovel.vue'
import ScrollLoadingIndicator from './components/ScrollLoadingIndicator.vue'
import type { ScrollChapter } from './composables/useInfiniteChapter'
import BizPopupBookTOCDefault from '@/components/BizPopupBookTOC.vue'
import BizPopupBookTOCFlipnovel from '@/components/BizPopupBookTOC.flipnovel.vue'
import ChapterBreadcrumb from '@/pages/chapter/components/ChapterBreadcrumb.vue'
import ChapterLeadSentence from '@/pages/chapter/components/ChapterLeadSentence.vue'
import ChapterRecommend from '@/pages/chapter/components/ChapterRecommend.vue'
import ReaderTaboolaGuide from '@/pages/chapter/components/ReaderTaboolaGuide.vue'
import { CoinsSuccessPopup as ChapterCoinsSuccess, getChapterConfig } from '@/composables/payment'
import { $formatErrorMessage } from '@/helpers'
import { api } from '@/api'
import { CHAPTER_LOCK, ERROR_PAGE_LEVEL, VIP_TYPE } from '@/constants'
import { useToastStore, useUserStore } from '@/stores'
import { useBookshelf } from '@/composables/useBookshelf'
import { bannerShow, useBannerShow } from '@/composables/useBannerShow'
import { pageViewTrack, useBookRead, useChapterReadComplete, useElementClick } from '@/composables/tracks'
import { useBottomOverscroll } from '@/composables/useBottomOverscroll'
import { brand, theme as brandTheme } from '@/config/brands'
import { loadBrandComponent } from '@/utils/loadBrandConfig'
import { useFbqReadChapter } from '@/composables/fbq/useFbqReadChapter'
import { useFbqViewContent } from '@/composables/fbq/useFbqViewContent'
import { useFbqReadDuration } from '@/composables/fbq/useFbqReadDuration'
import { useReaderTaboolaGuide } from '@/pages/chapter/composables/useReaderTaboolaGuide'

const props = defineProps<{
  contentId: string
  chapterId: string
}>()

// 加载当前品牌配置
const config = getChapterConfig()

// 加载品牌专属主题设置弹窗
const ChapterThemeSettings = loadBrandComponent({
  rocnovel: ChapterThemeSettingsDefault,
  oceread: ChapterThemeSettingsDefault,
  talewave: ChapterThemeSettingsDefault,
  ficmoon: ChapterThemeSettingsDefault,
  novelarc: ChapterThemeSettingsDefault,
  storyella: ChapterThemeSettingsDefault,
  silktale: ChapterThemeSettingsDefault,
  pearla: ChapterThemeSettingsDefault,
  fictora: ChapterThemeSettingsDefault,
  marinovel: ChapterThemeSettingsDefault,
  flipnovel: ChapterThemeSettingsFlipnovel,
  moonpage: ChapterThemeSettingsFlipnovel,
  storlyn: ChapterThemeSettingsFlipnovel,
})
const ScrollChapterItem = loadBrandComponent({
  rocnovel: ScrollChapterItemDefault,
  oceread: ScrollChapterItemDefault,
  talewave: ScrollChapterItemDefault,
  ficmoon: ScrollChapterItemDefault,
  novelarc: ScrollChapterItemDefault,
  storyella: ScrollChapterItemDefault,
  silktale: ScrollChapterItemDefault,
  pearla: ScrollChapterItemDefault,
  fictora: ScrollChapterItemDefault,
  marinovel: ScrollChapterItemDefault,
  flipnovel: ScrollChapterItemFlipnovel,
  moonpage: ScrollChapterItemFlipnovel,
  storlyn: ScrollChapterItemFlipnovel,
})
const ChapterTOCPopup = loadBrandComponent({
  rocnovel: BizPopupBookTOCDefault,
  oceread: BizPopupBookTOCDefault,
  talewave: BizPopupBookTOCDefault,
  ficmoon: BizPopupBookTOCDefault,
  novelarc: BizPopupBookTOCDefault,
  storyella: BizPopupBookTOCDefault,
  silktale: BizPopupBookTOCDefault,
  pearla: BizPopupBookTOCDefault,
  fictora: BizPopupBookTOCDefault,
  marinovel: BizPopupBookTOCDefault,
  flipnovel: BizPopupBookTOCFlipnovel,
  moonpage: BizPopupBookTOCFlipnovel,
  storlyn: BizPopupBookTOCFlipnovel,
})

const route = useRoute()
const router = useRouter()
const { showReaderTaboolaGuide } = useReaderTaboolaGuide(route)
const { $toast } = useToastStore()
const userStore = useUserStore()
const isFlipnovel = brand === 'flipnovel' || brand === 'moonpage' || brand === 'storlyn' || brand === 'novaris'
const { chapterVisitTrack, readCompleteTrackDirect } = useChapterReadComplete()
const { elementClick } = useElementClick()
const { start: startReadTime, bookRead } = useBookRead()
const isTalewave = brand === 'talewave'
const chapterScrollMenuIconColor = computed(() => {
  if (!isFlipnovel)
    return ''

  if (brand === 'flipnovel' || brand === 'novaris')
    return 'var(--rr-text-strong)'

  return brandTheme.textTertiary
})
const chapterScrollMenuIconStyle = computed(() => {
  if (!isFlipnovel)
    return undefined

  return {
    color: chapterScrollMenuIconColor.value,
  }
})
const { reportReadChapter } = useFbqReadChapter()
const { reportViewContent } = useFbqViewContent()

const { startSession: startReadDurationSession } = useFbqReadDuration()
let readDurationSession: import('@/composables/fbq/useFbqReadDuration').ReadDurationSession | null = null

// 基础状态
const loading = ref(true)
const unlockChapterId = ref<string>('') // 用户点击解锁的章节 ID
const coinsSuccess = ref(false)
const book = ref<any>()
const discountPayInfo = ref<Record<string, any>>({
  discountOnceCoins: 0,
  bookImageUrl: '',
})
const containerRef = ref<HTMLElement>()
const chapterList = ref<any>([])
const landingList = ref<string[]>([])
const popupTOC = ref(false)

let firstPaidChapterNo: number | null = null
const popupTheme = ref(false)
const showCompleteRecommendLoading = ref(false)
const showRecommendChapterId = ref<string>('') // 需要显示推荐的章节 ID

// 加入书架（与书首页共享逻辑）
const { isInShelf, shelfLoading, toggle: onToggleShelf } = useBookshelf(book)

provide('containerRef', containerRef)
provide('chapterConfig', config)

// 使用主题管理 composable
const { theme, THEME_FONT, currentSkinBg } = useChapterTheme(config.themeDefaults)
provide('theme', theme)

// 使用无限滚动章节加载 composable
const {
  chapters,
  loadingNext,
  loadingPrev,
  hasMoreNext,
  hasMorePrev,
  loadNextChapter,
  loadPrevChapter,
  unlockChapter,
  scrollToChapter,
  init: initChapters,
} = useInfiniteChapter({
  contentId: computed(() => props.contentId),
  initialChapterId: computed(() => props.chapterId),
  book,
  chapterList,
  containerRef,
})

// 埋点状态
const chapterReadState = ref<Map<string, {
  visitReported: boolean
  completeReported: boolean
  fbqReadReported: boolean
  enterTime: number
}>>(new Map())
const prevVisibleChapterId = ref<string>('')

// 章节进入可视区域
function onChapterEnter(chapter: ScrollChapter) {
  const state = chapterReadState.value.get(chapter.id) || {
    visitReported: false,
    completeReported: false,
    fbqReadReported: false,
    enterTime: 0,
  }

  state.enterTime = Date.now()

  if (!state.fbqReadReported && chapter.lockStatus !== CHAPTER_LOCK.LOCK) {
    reportReadChapter(props.contentId, chapter.id, chapter.sequenceNo, firstPaidChapterNo)
    state.fbqReadReported = true
  }

  // 上报章节访问（每章只上报一次，仅限未锁定章节）
  if (!state.visitReported && chapter.lockStatus !== CHAPTER_LOCK.LOCK) {
    chapterVisitTrack(props.contentId, chapter.id, chapter.sequenceNo, chapter.chapterVersionId)
    state.visitReported = true
  }

  chapterReadState.value.set(chapter.id, state)
}

// 章节离开可视区域
function onChapterLeave(chapter: ScrollChapter, direction: 'up' | 'down') {
  const state = chapterReadState.value.get(chapter.id)
  if (!state)
    return

  // 向上滚出表示该章节已读完
  if (direction === 'up' && !state.completeReported && chapter.lockStatus !== CHAPTER_LOCK.LOCK) {
    readCompleteTrackDirect(props.contentId, chapter.id, chapter.sequenceNo, chapter.chapterVersionId)
    state.completeReported = true
  }

  // 上报 biz_bookRead
  if (book.value) {
    bookRead({
      book_id: book.value.id,
      book_name: book.value.contentName,
      author: book.value?.authorInfo?.author,
      chapter_id: chapter.id,
      chapter_versionid: chapter.chapterVersionId,
      chapter_name: chapter.name,
      is_chargeable: chapter.feeType,
      serial_status: book.value.finishStatus === 0 ? 'ongoing' : 'completed',
      chapter_price: chapter.priceAmount,
      currency_coins: userStore.$state.coinBalance,
    })
  }
}

// 当前可见章节变化
function onCurrentChapterChange(newChapterId: string) {
  if (!prevVisibleChapterId.value) {
    prevVisibleChapterId.value = newChapterId
    return
  }

  // 找到新旧章节在列表中的位置
  const oldIndex = chapters.value.findIndex(c => c.id === prevVisibleChapterId.value)
  const newIndex = chapters.value.findIndex(c => c.id === newChapterId)

  if (oldIndex !== -1 && newIndex !== -1) {
    if (newIndex > oldIndex) {
      // 向下滚动到下一章
      elementClick('next_chapter')
    }
    else if (newIndex < oldIndex) {
      // 向上滚动到上一章
      elementClick('prev_chapter')
    }
  }

  // 章节切换时关闭主题设置弹窗
  popupTheme.value = false

  // 用户激活上报
  attributionTrack()

  prevVisibleChapterId.value = newChapterId
}

// 使用章节可见性检测 composable
const { currentChapterId, refresh: refreshVisibility, pause: pauseVisibility, resume: resumeVisibility, determineCurrentChapter } = useChapterVisibility({
  chapters,
  containerRef,
  contentId: computed(() => props.contentId),
  onChapterEnter,
  onChapterLeave,
  onCurrentChapterChange,
})

// 当前章节对象
const currentChapter = computed(() => {
  return chapters.value.find(c => c.id === currentChapterId.value)
})

const isCurrentChapterLocked = computed(() =>
  currentChapter.value?.lockStatus === CHAPTER_LOCK.LOCK,
)

// 末章是否为锁定章（付费墙）：此时不算"读到全书最后一章"
const isLockedTail = computed(() =>
  chapters.value[chapters.value.length - 1]?.lockStatus === CHAPTER_LOCK.LOCK,
)

// 滚动相关
const { y, arrivedState } = useScroll(containerRef)

// 是否是最后一章（用于底部过度滚动跳推荐）；付费墙锁定章不触发
const isLastChapter = computed(() => !hasMoreNext.value && !isLockedTail.value)

// 底部过度滚动检测（用于跳转推荐页）
const { startListen: startOverscrollListen, stopListen: stopOverscrollListen } = useBottomOverscroll({
  containerRef,
  isAtBottom: computed(() => arrivedState.bottom),
  enabled: isLastChapter,
  threshold: 80,
  onTrigger: finishRecommend,
})

// 错误处理
function onErrorHandle() {
  router.replace({
    name: 'error',
    query: {
      level: ERROR_PAGE_LEVEL.DEFAULT,
      message: 'Network loading failure',
      redirect: window.encodeURIComponent(window.location.href),
    },
  })
}

/**
 * 获取书籍信息
 */
async function fetchBook() {
  const { data } = await api.get<any, GetContentGetcontentdetailResponse>('/content/getContentDetail', { params: { contentId: props.contentId } })
  book.value = data
  firstPaidChapterNo = (data as any).needPayChapterMinNo ?? null
  isInShelf.value = !!data.bookShelf

  // 支付成功且类型是折扣充值则显示成功弹窗
  if (route.query.payment_status === 'success' && Number(route.query?.subscribeType) === VIP_TYPE.DISCOUNT && userStore.$state.landingPageId && userStore.$state.landingPageContentId === props.contentId) {
    coinsSuccess.value = true
    discountPayInfo.value.discountOnceCoins = route.query.discountOnceCoins
    discountPayInfo.value.bookImageUrl = data.contentCoverUrl
    pageViewTrack('sale')
  }
}

/**
 * 获取章节目录
 */
async function fetchChapterList() {
  try {
    const { data: chapterListData } = await api.get<any>('/content/getContentChapterBookList', {
      params: {
        contentId: props.contentId,
        pageIndex: 1,
        pageSize: 1500,
      },
    })
    chapterList.value = chapterListData.records
  }
  catch (error) {
    onErrorHandle()
  }
}

/**
 * 获取引导语句
 */
async function getLeadSentence() {
  const { data } = await api.get<any, GetContentGetLeadSentenceResponse>(`/content/getLeadSentence?contentId=${props.contentId}`)
  if (!data)
    return
  landingList.value = data?.split('\n')?.filter(Boolean) || []
}

/**
 * 获取最后的阅读位置，并替换路由为正确的 chapterId
 * 用于解决 book.firstChapter.id（加密格式）与章节列表 id（数字格式）不一致的问题
 */
async function fetchLatestPosition(): Promise<boolean> {
  const { data } = await api.post<any, PostReadPosResponse>('/read/pos', { contentId: book.value!.id })

  // 如果当前 chapterId 与服务器返回的不同，替换路由
  if (props.chapterId !== data.contentChapterId) {
    router.replace({
      name: 'chapter-scroll',
      params: { contentId: book.value!.id, chapterId: data.contentChapterId },
      query: route.query,
    })
    return true // 路由已替换，需要等待 watch 重新初始化
  }

  return false
}

/**
 * 用户激活上报
 */
async function attributionTrack() {
  if (localStorage.getItem('IsActive'))
    return

  try {
    const res = await api.get<any, any>('/member/active')
    if (res.code === 0)
      localStorage.setItem('IsActive', '1')
  }
  catch {
    // 静默失败
  }
}

/**
 * 页面关闭时的处理（beforeunload 事件）
 * 确保用户直接关闭页面时也能上报阅读数据
 */
function handleCloseEvent() {
  if (currentChapter.value && book.value) {
    bookRead({
      book_id: book.value.id,
      book_name: book.value.contentName,
      author: book.value?.authorInfo?.author,
      chapter_id: currentChapter.value.id,
      chapter_versionid: currentChapter.value.chapterVersionId,
      chapter_name: currentChapter.value.name,
      is_chargeable: currentChapter.value.feeType,
      serial_status: book.value.finishStatus === 0 ? 'ongoing' : 'completed',
      chapter_price: currentChapter.value.priceAmount,
      currency_coins: userStore.$state.coinBalance,
    })
  }
  return true
}

/**
 * 点击书名 → 跳转书首页
 */
function onClickBook() {
  router.push({ name: 'book', params: { contentId: props.contentId } })
}

/**
 * 打开目录
 */
function onOpenTOC() {
  popupTheme.value = false
  popupTOC.value = true
  attributionTrack()
}

/**
 * 目录章节点击检查
 * 如果章节已加载，直接滚动到该章节；否则允许路由跳转
 */
function handleTOCChapterClick(sequenceNo: number): boolean {
  const targetChapter = chapters.value.find(c => c.sequenceNo === sequenceNo)

  if (targetChapter) {
    // 章节已加载，直接滚动
    popupTOC.value = false

    if (targetChapter.sequenceNo === 1) {
      // 第一章，滚动到顶部显示导语
      if (containerRef.value)
        containerRef.value.scrollTop = 0
    }
    else {
      scrollToChapter(targetChapter.id)
    }

    return false // 阻止路由跳转
  }

  // 章节未加载，允许路由跳转重新初始化
  return true
}

/**
 * 上一章
 */
function onPrevChapter() {
  if (!currentChapter.value?.prevId)
    return

  // 关闭主题设置弹窗
  popupTheme.value = false

  elementClick('prev_chapter')

  // 检查上一章是否已加载
  const prevChapter = chapters.value.find(c => c.id === currentChapter.value!.prevId)
  if (prevChapter) {
    // 已加载，直接滚动到该章节
    scrollToChapter(prevChapter.id)
  }
  else {
    // 未加载，通过路由跳转重新初始化，保留现有 query 参数
    router.replace({ name: 'chapter-scroll', params: { contentId: props.contentId, chapterId: currentChapter.value.prevId }, query: route.query })
  }
}

/**
 * 下一章
 */
function onNextChapter() {
  // 关闭主题设置弹窗
  popupTheme.value = false

  // 如果 currentChapter 未设置（例如导语占满视口），默认使用第一个章节
  const chapter = currentChapter.value || chapters.value[0]

  // 用户激活上报
  attributionTrack()
  // 完读上报
  if (chapter && chapter.lockStatus !== CHAPTER_LOCK.LOCK)
    readCompleteTrackDirect(props.contentId, chapter.id, chapter.sequenceNo, chapter.chapterVersionId)

  elementClick('next_chapter')

  if (!chapter?.nextId) {
    // 无下一章，已是最后一章
    return
  }

  // 检查下一章是否已加载
  const nextChapter = chapters.value.find(c => c.id === chapter.nextId)
  if (nextChapter) {
    // 已加载，直接滚动到该章节
    scrollToChapter(nextChapter.id)
  }
  else {
    // 未加载，通过路由跳转重新初始化，保留现有 query 参数
    router.replace({ name: 'chapter-scroll', params: { contentId: props.contentId, chapterId: chapter.nextId }, query: route.query })
  }
}

/**
 * 切换主题设置
 */
function onToggleTheme() {
  popupTOC.value = false
  // 锁章页也允许点击切换：用于唤起/收起顶部导航栏；底部设置面板由 themeSettingsVisible 单独门控（锁章时不展示）
  popupTheme.value = !popupTheme.value
}

// 底部设置面板可见性：锁章页强制隐藏，仅非锁章且 popupTheme 时展示
const themeSettingsVisible = computed({
  get: () => popupTheme.value && !isCurrentChapterLocked.value,
  set: (val: boolean) => {
    popupTheme.value = val
  },
})

/**
 * 完读跳转推荐页
 */
function finishRecommend() {
  if (showCompleteRecommendLoading.value)
    return
  showCompleteRecommendLoading.value = true
  setTimeout(() => {
    router.push({
      name: 'recommend',
      params: { contentId: props.contentId },
      query: { status: book.value?.finishStatus === 0 ? 'ongoing' : 'finish' },
    })
  }, 800)
}

/**
 * 解锁章节（仅当余额足够时由 ScrollChapterUnlock 的 Unlock 按钮触发；
 * 余额不足时由常驻 PaymentInlinePanel 直接走支付流程，不再经过此函数）
 */
async function handleUnlock(chapterId: string, autoLock: boolean) {
  const chapter = chapters.value.find(c => c.id === chapterId)
  if (!chapter)
    return

  unlockChapterId.value = chapterId

  // 检查并更新 autoLock 状态
  if (autoLock !== undefined && autoLock !== userStore.$state.autoLock) {
    await api.post('/member/update/auto-lock', { autoLock })
    await userStore.init({ force: true })
  }

  await unlockChapter(chapterId)
}

/**
 * 处理充值成功
 */
function handleCoinsSuccess() {
  coinsSuccess.value = false
  const url = new URL(window.location.href)
  url.searchParams.delete('subscribeType')
  url.searchParams.delete('discountOnceCoins')
  history.replaceState({}, '', url.href)
}

async function handlePaymentSuccess(chapterId?: string) {
  await userStore.init({ force: true })
  const targetChapterId = chapterId || unlockChapterId.value || currentChapterId.value || props.chapterId
  if (targetChapterId)
    await unlockChapter(targetChapterId)

  const url = new URL(window.location.href)
  url.searchParams.delete('payment_status')
  url.searchParams.delete('type')
  history.replaceState({}, '', url.href)
}

/**
 * 上报滚动位置
 */
async function reportScrollLocation() {
  if (!currentChapterId.value || !containerRef.value)
    return

  const chapter = chapters.value.find(c => c.id === currentChapterId.value)
  if (!chapter)
    return

  // 计算当前章节在容器中的相对滚动位置
  const chapterEl = containerRef.value.querySelector(`[data-chapter-id="${currentChapterId.value}"]`) as HTMLElement
  if (!chapterEl)
    return

  const containerScrollTop = containerRef.value.scrollTop
  const chapterTop = chapterEl.offsetTop
  const chapterHeight = chapterEl.offsetHeight
  const containerHeight = containerRef.value.clientHeight

  // 计算章节内的滚动位置
  const scrollInChapter = Math.max(0, containerScrollTop - chapterTop)
  const totalScrollable = Math.max(0, chapterHeight - containerHeight)

  try {
    await api.post('/read/report', {
      contentId: props.contentId,
      chapterId: currentChapterId.value,
      chapterVersionId: chapter.chapterVersionId,
      sectionMediaPlayedSeconds: Math.floor(scrollInChapter),
      videoDuration: totalScrollable,
    })
  }
  catch {
    // 静默失败
  }
}

// Mounted
onMounted(async () => {
  readDurationSession = startReadDurationSession(props.contentId, props.chapterId)

  // 如果检测到页面是从"往返缓存"中读取的,刷新页面
  window.addEventListener('pageshow', (e) => {
    if (e.persisted)
      window.location.reload()
  })

  // 页面直接关闭时,需上报阅读事件
  window.addEventListener('beforeunload', handleCloseEvent)

  loading.value = true
  try {
    getLeadSentence()
    await fetchBook()
    await fetchChapterList()
    reportViewContent(props.contentId, book.value?.contentName, 'chapter', props.chapterId)

    // 如果没有 skipPosition 参数，先获取正确的 chapterId
    // 用于解决 book.firstChapter.id（加密格式）与章节列表 id（数字格式）不一致的问题
    if (!route.query.skipPosition) {
      const replaced = await fetchLatestPosition()
      if (replaced)
        return // 路由已替换，watch 会重新触发初始化
    }

    await initChapters()
    startReadTime.value = new Date().getTime()
  }
  catch (error) {
    $toast($formatErrorMessage(error))
  }
  finally {
    loading.value = false
  }

  // 等待 DOM 渲染后再滚动到初始章节并刷新可见性检测
  await nextTick()
  // 如果是第一章，保持在顶部显示导语；否则滚动到对应章节
  const initialChapter = chapters.value.find(c => c.id === props.chapterId)
  if (initialChapter && initialChapter.sequenceNo !== 1)
    scrollToChapter(props.chapterId)
  setTimeout(() => {
    refreshVisibility()
    // 初始化后立即计算当前可见章节，确保 currentChapterId 有值
    determineCurrentChapter()
  }, 200)

  useBannerShow()
  // 启动底部过度滚动监听（需要在 DOM 渲染后）
  startOverscrollListen()

  // 从支付页面返回时，显示推荐
  if (route.query.recommend === '1' && !route.query.payment_status)
    showRecommendChapterId.value = route.query.recommendChapterId as string || ''
})

// Watches
watch(() => props.contentId, async () => {
  getLeadSentence()
  await fetchBook()
  reportViewContent(props.contentId, book.value?.contentName, 'chapter', props.chapterId)
  fetchChapterList()
})

// 监听 chapterId 变化，重新初始化章节（用于目录跳转）
watch(() => props.chapterId, async (newChapterId, oldChapterId) => {
  if (newChapterId && newChapterId !== oldChapterId && !loading.value) {
    // 显示 loading（修复白屏问题）
    loading.value = true
    // 暂停可见性检测（修复跳转闪烁问题）
    pauseVisibility()

    // 关闭目录弹窗
    popupTOC.value = false

    // 清除埋点状态
    chapterReadState.value.clear()
    prevVisibleChapterId.value = ''

    try {
      // 重新初始化章节
      await initChapters()
    }
    finally {
      // 隐藏 loading
      loading.value = false
    }

    // 等待 DOM 渲染后再滚动到目标章节
    await nextTick()
    // 如果是第一章，保持在顶部显示导语；否则滚动到对应章节
    const targetChapter = chapters.value.find(c => c.id === newChapterId)
    if (targetChapter && targetChapter.sequenceNo !== 1)
      scrollToChapter(newChapterId)

    // 刷新可见性检测并恢复
    setTimeout(() => {
      refreshVisibility()
      // 恢复可见性检测（会自动调用 determineCurrentChapter）
      resumeVisibility()
      // 重新绑定底部过度滚动监听（因为 v-if 切换导致 DOM 重建）
      stopOverscrollListen()
      startOverscrollListen()
    }, 200)
  }
})

// 支付回跳自动解锁（统一覆盖所有品牌，替代原 popupRecharge watch 的支付成功分支 + onMounted 的 talewave 检查）
watch(
  () => route.query.payment_status,
  async (val) => {
    if (val !== 'success')
      return
    if (isTalewave) {
      await handlePaymentSuccess()
    }
    else {
      await userStore.init({ force: true })
      const targetChapterId = unlockChapterId.value || currentChapterId.value || props.chapterId
      if (targetChapterId)
        await unlockChapter(targetChapterId)
      const url = new URL(window.location.href)
      url.searchParams.delete('payment_status')
      history.replaceState({}, '', url.href)
    }
    unlockChapterId.value = ''
  },
  { immediate: true },
)

// 支付成功后展示推荐流程（由 PaymentInlinePanel @success 触发）
function handlePaySuccess(chapterId: string) {
  const actualChapterId = chapterId || unlockChapterId.value || currentChapterId.value || props.chapterId
  if (!actualChapterId)
    return

  showRecommendChapterId.value = actualChapterId
  router.replace({
    path: `/content/${props.contentId}/scroll/${actualChapterId}`,
    query: { ...route.query, recommend: '1', recommendChapterId: actualChapterId },
  })
}

// 监听 recommend 参数变化
watch(() => route.query.recommend, (val) => {
  if (!val)
    showRecommendChapterId.value = ''
})

/**
 * 推荐模块点击时保持 recommend 参数
 */
function appendRecommendParams() {
  const url = new URL(window.location.href)
  url.searchParams.set('recommend', '1')
  // 保留 recommendChapterId 参数
  if (showRecommendChapterId.value)
    url.searchParams.set('recommendChapterId', showRecommendChapterId.value)

  history.replaceState({}, '', url.href)
}

// 滚动监听
watchThrottled(y, async () => {
  // 上报滚动位置
  reportScrollLocation()

  // 更新当前可见章节（修复：滚动时更新章节）
  determineCurrentChapter()

  // 接近底部时加载下一章
  if (arrivedState.bottom && hasMoreNext.value)
    loadNextChapter()
  // 注：最后一章的推荐页跳转由 useBottomOverscroll 处理（需要用户继续向下拉才触发）

  // 接近顶部时加载上一章
  if (y.value < 100 && hasMorePrev.value)
    loadPrevChapter()
}, { throttle: 500, leading: true })

// Unmounted
onUnmounted(() => {
  readDurationSession?.endSession()
  // 页面卸载时上报当前章节的阅读数据
  if (currentChapter.value && book.value) {
    bookRead({
      book_id: book.value.id,
      book_name: book.value.contentName,
      author: book.value?.authorInfo?.author,
      chapter_id: currentChapter.value.id,
      chapter_versionid: currentChapter.value.chapterVersionId,
      chapter_name: currentChapter.value.name,
      is_chargeable: currentChapter.value.feeType,
      serial_status: book.value.finishStatus === 0 ? 'ongoing' : 'completed',
      chapter_price: currentChapter.value.priceAmount,
      currency_coins: userStore.$state.coinBalance,
    })
  }

  window.removeEventListener('beforeunload', handleCloseEvent)
})
</script>

<template>
  <div :class="`brand-${brand}`">
    <!-- 加载中 -->
    <div v-if="loading" class="min-h-100dvh flex justify-center items-center" :style="`background: ${theme.background}`">
      <BasicSpinner class="mx-auto" />
    </div>

    <!-- 主容器 -->
    <div v-else>
      <div
        ref="containerRef"
        :class="`h-100dvh overflow-scroll color-${theme.background.replace('#', '')}`"
        :style="`background: ${theme.background}; --theme-background: ${theme.background}`"
      >
        <!-- 皮肤背景图 -->
        <img
          v-if="currentSkinBg"
          :src="currentSkinBg"
          class="fixed top-0 right-0 w-252px h-310px pointer-events-none object-cover"
          :class="isFlipnovel ? 'rr-scroll-skin-bg' : 'z-10'"
          alt=""
        >
        <!-- 下载 App Banner -->
        <BizDownloadApp
          v-if="bannerShow && currentChapter"
          :id="contentId"
          position="fixed"
          class="fixed top-0 w-full"
          :index="currentChapter?.sequenceNo"
          from="chapter"
        />

        <!-- 导航栏（点击屏幕唤起/收起） -->
        <transition name="rr-topbar-slide">
          <div
            v-show="popupTheme"
            class="fixed w-full z-50"
            :class="{
              'top-60px': bannerShow,
              'top-0': !bannerShow,
              'rr-scroll-nav-open': popupTheme,
            }"
            :style="`transform: translate(0);${popupTheme ? `background: ${brandTheme.bgCard || '#1D232D'};` : ''}`"
          >
            <ChapterBreadcrumb
              :book-name="book?.contentName"
              :chapter-name="currentChapter?.name"
              :is-in-shelf="isInShelf"
              :shelf-loading="shelfLoading"
              @click-book="onClickBook"
              @toggle-shelf="onToggleShelf"
            />
          </div>
        </transition>

        <!-- 顶部加载指示器 -->
        <ScrollLoadingIndicator v-if="loadingPrev" position="top" />

        <!-- 章节列表 -->
        <div
          class="pt-10"
          :class="{ 'pt-24': bannerShow }"
          @click="onToggleTheme"
        >
          <!-- 引导语（仅在第一章前显示） -->
          <ChapterLeadSentence
            v-if="chapters.length > 0 && chapters[0].sequenceNo === 1"
            :landing-list="landingList"
            :sequence-no="1"
            :lead-sentence-style="THEME_FONT.leadSentence"
            class="mx-20px"
          />

          <div
            v-for="(chapter, index) in chapters"
            :key="chapter.id"
            :data-chapter-id="chapter.id"
          >
            <ScrollChapterItem
              :content-id="contentId"
              :chapter="chapter"
              :theme-font="THEME_FONT"
              :member-coin="userStore.$state.coinBalance"
              :is-last-chapter="!hasMoreNext && index === chapters.length - 1"
              :book-name="book?.contentName"
              :serial-status="book?.finishStatus"
              @unlock="handleUnlock"
              @pay-success="handlePaySuccess(chapter.id)"
            />

            <!-- 推荐模块：在触发充值的章节后显示 -->
            <ChapterRecommend
              v-if="showRecommendChapterId === chapter.id"
              class="mx-20px mt-24px"
              :book-id="contentId"
              @read="appendRecommendParams"
              @collection="appendRecommendParams"
              @error="showRecommendChapterId = ''"
            />
          </div>
        </div>

        <!-- 底部加载指示器 -->
        <ScrollLoadingIndicator v-if="loadingNext" position="bottom" />

        <!-- 完读加载中 -->
        <div v-if="showCompleteRecommendLoading" class="fixed top-0 left-0 flex justify-center items-center w-full h-full z-100">
          <BasicSpinner />
        </div>

        <!-- 主题设置弹窗 -->
        <ChapterThemeSettings
          v-model="themeSettingsVisible"
          :chapter="currentChapter"
          @prev-chapter="onPrevChapter"
          @next-chapter="onNextChapter"
          @open-toc="onOpenTOC"
        />

        <!-- 目录弹窗 -->
        <ChapterTOCPopup
          v-model="popupTOC"
          :book="book"
          :content-id="contentId"
          :chapter-id="currentChapterId || chapterId"
          route-name="chapter-scroll"
          :check-pass-goto-chapter="handleTOCChapterClick"
        />
      </div>

      <!-- 充值成功弹窗 -->
      <ChapterCoinsSuccess
        v-model="coinsSuccess"
        :discount-pay-info="discountPayInfo"
        @close="handleCoinsSuccess"
      />

      <ReaderTaboolaGuide v-model="showReaderTaboolaGuide" />
    </div>
  </div>
</template>

<style lang="scss">
@import './styles/chapter-theme.scss';
</style>

<style scoped lang="scss">
.rr-scroll-nav-open {
  background: var(--theme-background) !important;
  transition: background-color 0.22s ease, opacity 0.22s ease;
}

.rr-scroll-skin-bg {
  z-index: 1;
}

.rr-scroll-theme-topbar {
  min-height: 44px;
}

.rr-topbar-slide-enter-active,
.rr-topbar-slide-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}

.rr-topbar-slide-enter,
.rr-topbar-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
